import { AxiosResponse } from "axios";
import { apiCall } from "./api.service";

/** LMS success body: `{ data: T }` from sendYavrioSuccess */
/** LMS DD error body: `{ success: false, error: { code, message, details? } }` */

export interface Originator {
  sortCode: string;
  accountNumber: string;
  accountName: string;
}

export interface Destination {
  sortCode: string;
  accountNumber: string;
  accountName: string;
}

export interface DestinationContact {
  businessName: string;
  email: string;
}

export interface OriginatorContact {
  companyName: string;
  addressLine1: string | null;
  addressLine2: string | null;
}

export interface ModulusCheck {
  accountNumber?: string;
  sortCode?: string;
  bankName?: string;
  branchTitle?: string;
  branchAddress1?: string;
  branchAddress2?: string;
  branchAddress3?: string;
  branchAddress4?: string;
  branchAddressTown?: string;
  branchAddressPostCode?: string;
  telephoneNumber?: string;
  sortCodeOK?: boolean;
  accountCodeOK?: boolean;
  directDebitsOK?: boolean;
  directDebitInstructionOK?: boolean;
  creditsAllowedOK?: boolean;
  bacsAllowedOK?: boolean;
  error?: string;
  checked: boolean;
}

export interface MandateResponse {
  mandateId: string;
  paymentSystem: string;
  auddisReference: string;
  serviceUserNumber: string;
  originator?: Originator;
  originatorContact?: OriginatorContact;
  destination?: Destination;
  destinationContact?: DestinationContact;
  createdAt: string;
  modulusCheck?: ModulusCheck;
  status?: string;
  statusDescription?: string;
}

export interface SignMandateRequest {
  signature: string;
  soleSignatory: boolean;
  signatureType: "ELECTRONIC" | "PHYSICAL";
}

export interface SignMandateResponse {
  mandateId: string;
  status: string;
  statusDescription: string;
  signedAt?: string;
  signed: boolean;
  signatureType?: string;
}

export interface UploadMandateResponse {
  mandateId: string;
  status: string;
  statusDescription: string;
  uploadedAt?: string;
  fileName?: string;
  fileSize?: number;
  signed: boolean;
  signatureType?: string;
  soleSignatory?: boolean;
  message?: string;
}

export type MandateApiResult<T> =
  | { ok: true; data: T }
  | {
      ok: false;
      status: number;
      code?: string;
      message?: string;
      details?: Record<string, unknown> | null;
    };

export type MandateApiFailure = Extract<MandateApiResult<unknown>, { ok: false }>;

function readErrorPayload(data: unknown): {
  code?: string;
  message?: string;
  details?: Record<string, unknown> | null;
} {
  if (!data || typeof data !== "object") {
    return {};
  }
  const d = data as Record<string, unknown>;
  if (d.success === false && d.error && typeof d.error === "object") {
    const e = d.error as Record<string, unknown>;
    return {
      code: typeof e.code === "string" ? e.code : undefined,
      message: typeof e.message === "string" ? e.message : undefined,
      details:
        e.details != null && typeof e.details === "object"
          ? (e.details as Record<string, unknown>)
          : null,
    };
  }
  return {};
}

export function mandateResultFromAxios<T>(
  response: AxiosResponse
): MandateApiResult<T> {
  const status = response.status;
  if (status >= 200 && status < 300) {
    const body = response.data as Record<string, unknown> | undefined;
    const payload = body?.data as T;
    return { ok: true, data: payload };
  }
  const err = readErrorPayload(response.data);
  return {
    ok: false,
    status,
    code: err.code,
    message: err.message,
    details: err.details,
  };
}

export function signingIssueFromErrorResult(
  result: MandateApiFailure
):
  | "expired"
  | "invalid"
  | "other" {
  if (result.code === "EXPIRED_TOKEN") {
    return "expired";
  }
  if (result.code === "INVALID_TOKEN") {
    return "invalid";
  }
  if (result.status === 401) {
    return "invalid";
  }
  return "other";
}

export function originatorNameFromDetails(
  details?: Record<string, unknown> | null
): string | undefined {
  if (!details) {
    return undefined;
  }
  const n = details.originatorCompanyName;
  return typeof n === "string" && n.trim() ? n.trim() : undefined;
}

export const getMandate = async (
  token: string
): Promise<MandateApiResult<MandateResponse>> => {
  const response = await apiCall(
    "GET",
    `bacs/mandates/sign?token=${encodeURIComponent(token)}`
  );
  return mandateResultFromAxios<MandateResponse>(response);
};

export const signMandate = async (
  token: string,
  signData: SignMandateRequest
): Promise<MandateApiResult<SignMandateResponse>> => {
  const response = await apiCall(
    "POST",
    `bacs/mandates/sign?token=${encodeURIComponent(token)}`,
    signData
  );
  return mandateResultFromAxios<SignMandateResponse>(response);
};

export const uploadMandate = async (
  token: string,
  file: File,
  soleSignatory: boolean,
  signature: string,
  onProgress?: (progress: number) => void
): Promise<MandateApiResult<UploadMandateResponse>> => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("soleSignatory", soleSignatory.toString());
    formData.append("signature", signature);
    formData.append("signatureType", "PHYSICAL");

    const response = await apiCall(
      "POST",
      `bacs/mandates/upload?token=${encodeURIComponent(token)}`,
      formData,
      null,
      {},
      onProgress
    );
    return mandateResultFromAxios<UploadMandateResponse>(response);
  } catch (error) {
    console.error("Error uploading file:", error);
    throw error;
  }
};
